import { useState } from "react";
import { Check, Rocket, ShieldCheck, Users, Zap, Search } from "lucide-react";
import { services } from "../data/Services";

export default function ServicesPage() {
  const phoneNumber = '18093041588';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Extraer categorías únicas
  const categories = ['All', ...new Set(services.map(service => service.title))];

  // Filtrar servicios
  const filteredServices = services.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         service.features.some(feature => 
                             feature.toLowerCase().includes(searchQuery.toLowerCase())
                         );
    
    const matchesCategory = selectedCategory === 'All' || 
                          service.title === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const whatsappMessage = (service: string) => 
    encodeURIComponent(`Hola, estoy interesado en el servicio de ${service}`);

  const workflow = [
    { step: 1, title: "Consultation", description: "Free needs assessment session" },
    { step: 2, title: "Proposal", description: "Detailed project roadmap & quote" },
    { step: 3, title: "Execution", description: "Agile development with weekly updates" },
    { step: 4, title: "Delivery", description: "Final implementation & training" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-indigo-50">
      {/* Hero Section */}
      <section className="text-center py-20 px-4 bg-gradient-to-r from-navy-900 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-4xl font-bold mb-6">
            Beyond Templates - <span className="bg-gradient-to-r from-indigo-400 to-blue-400 bg-clip-text text-transparent">Full-Service Digital Solutions</span>
          </h1>
          <p className="text-xl text-indigo-100 max-w-2xl mx-auto">
            From initial concept to final implementation - we transform templates into powerful business tools
          </p>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <Rocket className="w-12 h-12 text-navy-900 mb-4" />
              <h3 className="text-xl font-bold mb-2">Time-Saving Solutions</h3>
              <p className="text-gray-600">Average 40% faster implementation than industry standards</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <Users className="w-12 h-12 text-navy-900 mb-4" />
              <h3 className="text-xl font-bold mb-2">Dedicated Teams</h3>
              <p className="text-gray-600">Personal project manager + specialized experts</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <Zap className="w-12 h-12 text-navy-900 mb-4" />
              <h3 className="text-xl font-bold mb-2">Rapid Deployment</h3>
              <p className="text-gray-600">72-hour turnaround for standard modifications</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <ShieldCheck className="w-12 h-12 text-navy-900 mb-4" />
              <h3 className="text-xl font-bold mb-2">Quality Assurance</h3>
              <p className="text-gray-600">Triple-check system with performance audits</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filtros */}
      <section className="py-8 px-4 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-1 w-full relative">
              <input
                type="text"
                placeholder="Buscar servicios..."
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="w-5 h-5 absolute right-3 top-3.5 text-gray-400" />
            </div>
            
            <select title="Category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full md:w-64 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {categories.map((category, index) => (
                <option key={index} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl text-navy-900 font-bold text-center mb-12">
            {filteredServices.length} Servicios Disponibles
          </h2>
          
          {filteredServices.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-block bg-indigo-50 rounded-full p-4 mb-4">
                <Search className="w-12 h-12 text-navy-900" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                No se encontraron servicios
              </h3>
              <p className="text-gray-600">
                Prueba ajustando tus términos de búsqueda o filtros
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredServices.map((service, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100 flex flex-col">
                  <div className="text-navy-900 mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-4 flex-grow">{service.description}</p>
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center text-gray-600 text-sm">
                        <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={`https://wa.me/${phoneNumber}?text=${whatsappMessage(service.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto bg-navy-900 text-white px-4 py-3 rounded-lg font-medium hover:bg-navy-700 transition-colors text-center flex items-center justify-center gap-2"
                  >
                    <WhatsAppIcon className="w-5 h-5" />
                    Consultar Servicio
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Workflow Process */}
      <section className="py-16 px-4 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Nuestro Proceso</h2>
          
          <div className="md:hidden">
            <div className="flex flex-col relative">
              <div className="absolute left-6 top-8 bottom-8 w-px bg-indigo-500/20"></div>
              {workflow.map((step, index) => (
                <div key={index} className="relative pl-16 mb-8 last:mb-0">
                  <div className="absolute left-0 top-0 w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center text-xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-indigo-200">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden md:grid grid-cols-4 gap-8">
            {workflow.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-indigo-500 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-indigo-200 px-4">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center bg-navy-900 rounded-2xl p-12 text-white shadow-2xl">
          <h2 className="text-3xl font-bold mb-6">¿Listo para potenciar tu presencia digital?</h2>
          <p className="text-xl mb-8">Agenda una consulta gratuita con nuestro arquitecto de soluciones</p>
          <a
            href={`https://wa.me/${phoneNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-bold hover:scale-105 transition-transform inline-flex items-center justify-center gap-2"
          >
            <Zap className="w-5 h-5" />
            Comienza tu proyecto ahora
          </a>
        </div>
      </section>
    </div>
  );
}

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 32 32" fill="currentColor">
    <path d="M16 2.667c-7.333 0-13.333 6-13.333 13.333 0 2.945.945 5.666 2.556 7.945L2.667 29.333l6.278-2.222c2.222 1.555 4.945 2.5 7.945 2.5 7.333 0 13.333-6 13.333-13.333S23.333 2.667 16 2.667zm7.111 17.778c-.333.555-2 .945-3.222.555-.333 0-1.111.167-3.778-1.222-2.222-1.167-3.666-3.666-3.778-3.833-.111-.167-.944-1.222-.944-2.389 0-1.166.555-1.722.778-1.945.222-.222.444-.333.722-.333h.555c.222 0 .444 0 .666.278.222.278.722 1 .778 1.055.055.055.111.111.222.166.111.056.222.056.333.056.222 0 .444-.111.722-.333.333-.278.666-.611 1.055-1 .722-.722 1.5-1.5 2.222-2.222.5-.5 1.055-1 1.722-1.333.666-.333 1.333-.278 1.722.111.389.389.722.833.722 1.389 0 .555-.055 1.055-.333 1.5-.278.444-.722.833-1.055 1.166-.333.333-.722.666-1.055 1-.333.333-.666.666-.5 1.111.167.444.722 1.833 1.222 2.5.5.666 1.055 1.222 1.722 1.722.666.5 1.333.945 2.055 1.222.722.278 1.389.278 1.833.056.444-.222.722-.666.944-1.111.222-.444.333-.944.333-1.389-.056-.555-.334-1-.668-1.333z"/>
  </svg>
);